/*
 * Enquadramento de imagens de capa (banner do agendamento e fotos dos cards da página pública).
 *
 * O backend guarda o ponto de foco (0 a 100% da imagem) e o zoom (100 a 200%), espelhando o
 * record ImageFraming. A mesma foto aparece em proporções diferentes no computador e no celular,
 * por isso o foco, e não um recorte fixo: o ponto escolhido fica visível em qualquer formato.
 */

export const IMAGE_FRAMING_MIN_ZOOM = 100;
export const IMAGE_FRAMING_MAX_ZOOM = 200;
export const IMAGE_FRAMING_CENTERED = Object.freeze({ positionX: 50, positionY: 50, zoom: IMAGE_FRAMING_MIN_ZOOM });

// Nulo e vazio contam como "sem ajuste"; Number(null) daria 0 e jogaria o foco para a borda.
const toNumber = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value));

// Mesmos limites do backend, para a prévia nunca mostrar algo que o servidor vai corrigir.
export const clampFramingPosition = (value) => {
    const number = toNumber(value);
    return Number.isFinite(number) ? Math.round(Math.max(0, Math.min(100, number))) : 50;
};

export const clampFramingZoom = (value) => {
    const number = toNumber(value);
    return Number.isFinite(number)
        ? Math.round(Math.max(IMAGE_FRAMING_MIN_ZOOM, Math.min(IMAGE_FRAMING_MAX_ZOOM, number)))
        : IMAGE_FRAMING_MIN_ZOOM;
};

/**
 * Estilo de um <img> que preenche a moldura (object-fit: cover) respeitando foco e zoom.
 *
 * Com object-position em X%, o ponto X% da imagem coincide com o ponto X% da moldura; o scale a
 * partir desse mesmo ponto aproxima sem tirá-lo do lugar e sem abrir borda vazia.
 */
export const getImageFramingStyle = ({ positionX = 50, positionY = 50, zoom = IMAGE_FRAMING_MIN_ZOOM } = {}) => {
    const x = clampFramingPosition(positionX);
    const y = clampFramingPosition(positionY);
    return {
        objectFit: 'cover',
        objectPosition: `${x}% ${y}%`,
        transform: `scale(${clampFramingZoom(zoom) / 100})`,
        transformOrigin: `${x}% ${y}%`,
    };
};

/**
 * Trecho da imagem (em % da largura e da altura dela) que aparece numa moldura de proporção
 * frameAspect (largura / altura), com o mesmo cálculo de getImageFramingStyle.
 *
 * Sem zoom, o cover mostra a imagem inteira num eixo e corta o outro; o zoom encolhe o trecho
 * visível nos dois eixos. Pelo object-position, o trecho começa em foco * (1 - tamanho visível).
 */
export const getVisibleImageArea = ({ imageAspect, frameAspect, positionX = 50, positionY = 50, zoom = IMAGE_FRAMING_MIN_ZOOM }) => {
    if (!imageAspect || !frameAspect) {
        return null;
    }

    const scale = clampFramingZoom(zoom) / 100;
    const width = (imageAspect > frameAspect ? frameAspect / imageAspect : 1) / scale;
    const height = (imageAspect > frameAspect ? 1 : imageAspect / frameAspect) / scale;
    const x = clampFramingPosition(positionX) / 100;
    const y = clampFramingPosition(positionY) / 100;

    return {
        left: x * (1 - width) * 100,
        top: y * (1 - height) * 100,
        width: width * 100,
        height: height * 100,
    };
};
